import ElectronicIcon from '@mui/icons-material/Fitbit';
import { useTranslation } from 'react-i18next';
import CodeContent, { CodeSnippet } from '../Shared/CodeContent';
import { electronics } from '../../../utils/constants';
import { getBitsElectronicIcon } from '../../../utils/getBitsElectronicIcon';

const ELECTRONICS_LIST = Object.values(electronics);

const ELECTRONICS_SNIPPET_FALLBACKS: Record<
  string,
  {
    title: string;
    code: string;
    description: string;
    moreInfo: string;
  }
> = {
  high_speed_motor: {
    title: 'High-Speed Motor',
    code: 'motor = HighSpeedMotor(port=1)',
    description: 'Sets up a high-speed motor for wheels, intakes, or flywheels.',
    moreInfo:
      'The High-Speed Motor is designed for faster rotation with lower torque. Use it when a mechanism needs speed, such as an intake, wheel, or flywheel launcher.\\n\\nCommon settings:\\n\\n• Connected port\\n\\n• Maximum torque\\n\\n• Motor direction\\n\\n• Motor stopping mode\\n\\nReturned values include velocity, position, current, voltage, temperature, and power output.',
  },
  high_torque_motor: {
    title: 'High-Torque Motor',
    code: 'motor = HighTorqueMotor(port=1)',
    description: 'Sets up a high-torque motor for arms, lifts, or mechanisms that need force.',
    moreInfo:
      'The High-Torque Motor is designed for more rotational force with lower speed. Use it for arms, lifts, levers, and mechanisms that need strength.\\n\\nCommon settings:\\n\\n• Connected port\\n\\n• Maximum torque: 0.01-0.93 N-m\\n\\n• Motor direction\\n\\n• Motor stopping mode\\n\\nReturned values include velocity, position, current, voltage, temperature, and power output.',
  },
  bee_bpr_adapter: {
    title: 'Bit Adapter',
    code: 'adapter = BitAdapter(port=1)',
    description: 'Controls connected Blueprint Engineering bits from the robot.',
    moreInfo:
      'The Bit Adapter lets the Drive Base power and control connected Blueprint Engineering bits. The robot can send output values from -100 to 100 to the connected bit. Communication is one-way, so the bit does not send sensor data back.',
  },
  beam_break_sensor: {
    title: 'Beam Break Sensor',
    code: 'beam = BeamBreakSensor(port=1)',
    description: 'Detects when an object blocks the sensor beam.',
    moreInfo:
      'The Beam Break Sensor reports whether an object is blocking its infrared beam. Use it to detect game pieces, mechanism limits, or objects passing through a known spot.\\n\\nReturned values include whether the beam is broken and the detected object distance.',
  },
  distance_sensor: {
    title: 'Distance Sensor',
    code: 'distance = DistanceSensor(port=1)',
    description: 'Measures how far away an object is.',
    moreInfo:
      'The Distance Sensor measures the distance to an object up to about 1 meter away. Use it when the robot needs to react to nearby objects, walls, or game pieces.\\n\\nReturned value:\\n\\n• Distance in meters',
  },
  magnetic_proximity_sensor: {
    title: 'Magnetic Proximity Switch',
    code: 'magnetic = MagneticProximitySwitch(port=1)',
    description: 'Detects when a magnet is near the sensor.',
    moreInfo:
      'The Magnetic Proximity Switch reports whether a compatible magnet is nearby. Use it as a mechanism limit sensor or to detect a known position.\\n\\nReturned value:\\n\\n• True when a magnet is detected, otherwise False',
  },
};

interface ElectronicsContentProps {
  title?: string;
  allowedTypes?: string[];
  snippetKey?: string;
}

const ElectronicsContent = ({ title = 'Electronics', allowedTypes, snippetKey = 'electronics' }: ElectronicsContentProps) => {
  const { t } = useTranslation();
  const filteredElectronics = allowedTypes
    ? ELECTRONICS_LIST.filter((electronic) => allowedTypes.includes(electronic.ELECTRONIC_TYPE))
    : ELECTRONICS_LIST;

  const children: CodeSnippet['children'] = filteredElectronics.map((electronic) => {
    const base = `snippets.electronics.children.${electronic.LOCIZE_TAG}`;
    const fallback = ELECTRONICS_SNIPPET_FALLBACKS[electronic.LOCIZE_TAG];
    const title = t(`${base}.title`, fallback?.title ?? electronic.ELECTRONIC_TYPE);
    const code = t(`${base}.code`, fallback?.code ?? '');
    const description = t(`${base}.description`, fallback?.description ?? '');
    const more_info = t(`${base}.more_info`, fallback?.moreInfo ?? '');
    const iconComponent = getBitsElectronicIcon(electronic.ELECTRONIC_TYPE);
    return {
      key: electronic.ELECTRONIC_TYPE,
      title,
      code,
      ...(description && { description }),
      ...(more_info && { more_info }),
      ...(iconComponent && { iconComponent }),
    };
  });

  const snippet: CodeSnippet = {
    key: snippetKey,
    title,
    icon: <ElectronicIcon />,
    type: 'electronic',
    children,
  };

  return <CodeContent snippet={snippet} commandManagementEnabled />;
};

export default ElectronicsContent;
